import { action, observable } from 'mobx';
import { liningMaterialOptions } from 'cd-eft/components/Design/Waterway/WDTContainer/WDTContentComp/WaterwayInput/WaterwayInputVM';
import { formatRound } from 'cd-eft/utils/mathUtilities';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';

export interface IWaterwayLiningMaterialUpdateProps {
  handleCloseLiningMaterialManualEntry: (...args: any[]) => void;
  handleSaveLiningMaterialManualEntry: (...args: any[]) => void;
  materialNameProp: string;
  manningProp: number;
  liningThicknessProp: number;
}

export class WaterwayLiningMaterialUpdateVM {
  @observable props;
  @observable manualEntryEnable: boolean = false;
  @observable selectedOption: string = '';
  @observable manualEntryName: string = '';
  @observable manning: number = 0;
  @observable thickness: number = 0;
  @observable formErrors = {
    manualEntryMaterial: '',
    manualEntryManning: '',
    manualEntryThickness: '',
    comboxSelection: '',
  };

  constructor(props: IWaterwayLiningMaterialUpdateProps) {
    this.props = props;
    const enableManual = !liningMaterialOptions.map((e) => e.value).includes(props.materialNameProp);
    this.manualEntryEnable = enableManual;
    this.selectedOption = enableManual ? 'Manual Entry' : props.materialNameProp;
    this.manualEntryName = enableManual ? props.materialNameProp : '';
    this.manning = props.manningProp;
    this.thickness = props.liningThicknessProp;
  }

  @action.bound setManualEntryEnable = (val: boolean) => (this.manualEntryEnable = val);
  @action.bound setSelectedOption = (val: string) => (this.selectedOption = val);
  @action.bound setManualEntryName = (val: string) => (this.manualEntryName = val);
  @action.bound setManning = (val: number) => (this.manning = val);
  @action.bound setThickness = (val: number) => (this.thickness = val);
  @action.bound setFormErrors = (val: any) => (this.formErrors = { ...val });

  @action.bound validateInputField = async (event) => {
    const {name, value} = event.target;
    const errors = { ...this.formErrors };
    let val;
    if (name === 'manual_entry_manning') {
      val = formatRound(parseFloat(value), 4);
      this.setManning(val);
    }
    switch (name) {
      case 'manual_entry_manning':
        if (value === '' || val < 0.0001 || val > 1.0) {
          await MessageCenter.addNotificationFromCode('Formula_02');
          errors.manualEntryManning = 'usa-input--error';
        } else {
          errors.manualEntryManning = '';
        }
        break;
      case 'manual_entry_thickness':
        if (value === '' || value < 0.1 || value > 30) {
          await MessageCenter.addNotificationFromCode('WDT_149');
          errors.manualEntryThickness = 'usa-input--error';
        } else {
          errors.manualEntryThickness = '';
        }
        break;
      case 'manual_entry_material':
        if (value === '') {
          errors.manualEntryMaterial = 'usa-input--error';
        } else {
          errors.manualEntryMaterial = '';
        }
        break;
      case 'liningMaterial':
        if (value === '') {
          errors.comboxSelection = 'usa-input--error';
        } else {
          errors.comboxSelection = '';
        }
        break;
      default:
        break;
    }
    this.setFormErrors(errors);
  };

  @action.bound handleManualEntryChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case 'manual_entry_material':
        this.setManualEntryName(value);
        break;
      case 'manual_entry_manning':
        if (value.length > 7) { // ask Bipin 
          return;
        } 
        this.setManning(value);
        break;
      case 'manual_entry_thickness':
        this.setThickness(value);
        break;
      default:
        break;
    }
  };

  @action.bound handleSave = async () => {
    const name = this.manualEntryEnable ? this.manualEntryName : this.selectedOption.split('(')[0].trim();
    await this.props.handleSaveLiningMaterialManualEntry(name, this.manning, this.thickness);
    this.handleClose();
  };

  @action.bound handleClose = () => {
    this.props.handleCloseLiningMaterialManualEntry();
  };

  @action.bound handleLiningMaterialChange = (event) => {
    const { value, name } = event.target;
    this.setSelectedOption(value);
    if (value === '') {
      this.setManning(0);
      this.setThickness(0);
      this.setManualEntryEnable(false);
    } else if (value === 'Manual Entry') {
      this.setManualEntryEnable(true);
      this.setManualEntryName('');
      this.setManning(0);
      this.setThickness(0);
    } else {
      this.setManualEntryEnable(false);
      this.setManning(liningMaterialOptions.filter((e) => e.value === value)[0].manning);
      this.setThickness(liningMaterialOptions.filter((e) => e.value === value)[0].liningThickness);
    }
  };

  // Check if the form has errors or empty fields
  isFormInvalid = () => {
    let res = false;
    if (this.manualEntryEnable) {
      res =
        this.manualEntryName === '' ||
        this.manning === 0 ||
        this.thickness === 0 ||
        this.formErrors.manualEntryMaterial !== '' ||
        this.formErrors.manualEntryManning !== '' ||
        this.formErrors.manualEntryThickness !== '';
    }
    return res;
  };
}
